import type { ClientInput, RouteDef, RouteResponse, Routes } from "./define";

export interface ClientOptions {
  baseUrl: string;
  fetch?: typeof fetch;
  headers?: () => Record<string, string> | Promise<Record<string, string>>;
  /**
   * Cookie policy. `"include"` (default) for the staff app, whose session is a cookie on a
   * sibling origin; `"omit"` for bearer-token clients (the Mini App, native apps) — a request
   * that carries credentials needs `Access-Control-Allow-Credentials` from the server, and a
   * bearer-only tier deliberately never sends that header.
   */
  credentials?: RequestCredentials;
}

export type Client<T extends Routes> = {
  [K in keyof T]: (input: ClientInput<T[K]>) => Promise<RouteResponse<T[K]>>;
};

function fillPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
    const value = params?.[key];
    if (value === undefined || value === null) throw new Error(`missing path param ${key}`);
    return encodeURIComponent(String(value));
  });
}

function toQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) for (const v of value) sp.append(key, String(v));
    else sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * One typed method per route. The response is `{ status, body }` discriminated by the
 * statuses the contract declares; callers switch on `status` instead of catching.
 */
export function createClient<T extends Routes>(routes: T, options: ClientOptions): Client<T> {
  const doFetch = options.fetch ?? fetch;
  const client = {} as Client<T>;
  for (const [name, route] of Object.entries(routes) as Array<[keyof T, RouteDef]>) {
    client[name] = (async (input: ClientInput<RouteDef>) => {
      const url =
        options.baseUrl +
        fillPath(route.path, input.params as Record<string, unknown> | undefined) +
        toQuery(input.query as Record<string, unknown> | undefined);
      const headers: Record<string, string> = {
        accept: "application/json",
        ...(await options.headers?.()),
      };
      const init: RequestInit = {
        method: route.method,
        headers,
        credentials: options.credentials ?? "include",
      };
      if (input.body !== undefined) {
        headers["content-type"] = "application/json";
        init.body = JSON.stringify(input.body);
      }
      const res = await doFetch(url, init);
      const text = await res.text();
      const body = text ? JSON.parse(text) : undefined;
      return { status: res.status, body } as RouteResponse<RouteDef>;
    }) as Client<T>[keyof T];
  }
  return client;
}
